import { useState } from 'react';

import { SlidersHorizontal, X } from 'lucide-react';

import { selectClass } from '@/components/data-connectors/MappingEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { filterChips } from './search-state';

import type { STOCK_SORTS, StockSearchState } from '@repo/shared-types';

const BODY = ['SUV', 'Hatchback', 'Estate', 'Saloon', 'Coupe', 'Convertible', 'MPV', 'Pickup'];
const FUEL = [
  'Petrol',
  'Diesel',
  'Electric',
  'Petrol Hybrid',
  'Petrol Plug-in Hybrid',
  'Diesel Hybrid',
];
const GEARBOX = ['Automatic', 'Manual'];
export const SORT_LABELS: Record<(typeof STOCK_SORTS)[number], string> = {
  relevance: 'Best match',
  price_desc: 'Price: high to low',
  price_asc: 'Price: low to high',
  mileage_asc: 'Mileage: low to high',
  mileage_desc: 'Mileage: high to low',
  year_desc: 'Newest first',
  year_asc: 'Oldest first',
  newest: 'Recently added',
  distance: 'Nearest first',
};

interface Props {
  state: StockSearchState;
  onChange: (next: StockSearchState) => void;
  source: string;
  onSourceChange: (source: string) => void;
  connectors: { id: string; name: string }[];
}

type TextKey = 'keywords' | 'make' | 'model' | 'colour' | 'postcode';
type NumKey = 'price_min' | 'price_max' | 'year_min' | 'year_max' | 'mileage_max' | 'radius_miles';
type ChoiceKey = 'body_type' | 'fuel_type' | 'transmission';

const field = 'space-y-1';
const label = 'text-muted-foreground text-xs font-medium';

/** Nearest-first needs a postcode, so drop it when the postcode is gone. */
const withValidSort = (s: StockSearchState): StockSearchState =>
  s.sort === 'distance' && !s.postcode ? { ...s, sort: undefined } : s;

export const StockFilters = ({ state, onChange, source, onSourceChange, connectors }: Props) => {
  // The popover edits a draft; the search only runs on Apply.
  const [draft, setDraft] = useState(state);
  const [open, setOpen] = useState(false);
  const [seenState, setSeenState] = useState(state);
  if (seenState !== state) {
    setSeenState(state);
    setDraft(state);
  }

  const setText = (key: TextKey, value: string) =>
    setDraft((d) => ({ ...d, [key]: value || undefined }));
  const setNum = (key: NumKey, value: string) =>
    setDraft((d) => ({ ...d, [key]: value === '' ? undefined : Math.max(Number(value), 0) }));
  const setChoice = (key: ChoiceKey, value: string) =>
    setDraft((d) => ({ ...d, [key]: value || undefined }));

  const chips = filterChips(state);
  const textInput = (key: TextKey, text: string, placeholder: string) => (
    <div className={field}>
      <label htmlFor={`f-${key}`} className={label}>
        {text}
      </label>
      <Input
        id={`f-${key}`}
        value={draft[key] ?? ''}
        placeholder={placeholder}
        onChange={(e) => setText(key, e.target.value)}
      />
    </div>
  );
  const numInput = (key: NumKey, text: string, placeholder: string) => (
    <div className={field}>
      <label htmlFor={`f-${key}`} className={label}>
        {text}
      </label>
      <Input
        id={`f-${key}`}
        type="number"
        inputMode="numeric"
        min={0}
        value={draft[key] ?? ''}
        placeholder={placeholder}
        onChange={(e) => setNum(key, e.target.value)}
      />
    </div>
  );
  const choice = (key: ChoiceKey, text: string, options: string[]) => (
    <div className={field}>
      <label htmlFor={`f-${key}`} className={label}>
        {text}
      </label>
      <select
        id={`f-${key}`}
        className={`${selectClass} w-full`}
        value={draft[key] ?? ''}
        onChange={(e) => setChoice(key, e.target.value)}
      >
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <SlidersHorizontal className="mr-1 h-4 w-4" aria-hidden />
              More filters
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(92vw,560px)]">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                onChange(withValidSort(draft));
                setOpen(false);
              }}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {textInput('keywords', 'Keywords', 'e.g. M Sport, LB68MHJ')}
                {textInput('make', 'Make', 'e.g. BMW')}
                {textInput('model', 'Model', 'e.g. 3 Series')}
                {choice('body_type', 'Body', BODY)}
                {choice('fuel_type', 'Fuel', FUEL)}
                {choice('transmission', 'Gearbox', GEARBOX)}
                {textInput('colour', 'Colour', 'e.g. Black')}
                {numInput('price_min', 'Price from (£)', '0')}
                {numInput('price_max', 'Price to (£)', 'Any')}
                {numInput('year_min', 'Year from', 'e.g. 2018')}
                {numInput('year_max', 'Year to', 'Any')}
                {numInput('mileage_max', 'Max mileage', 'Any')}
                {textInput('postcode', 'Postcode', 'e.g. B23 or LE10 3DJ')}
                {numInput('radius_miles', 'Radius (miles)', 'Auto')}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDraft({ sort: state.sort })}
                >
                  Reset
                </Button>
                <Button type="submit">Apply filters</Button>
              </div>
            </form>
          </PopoverContent>
        </Popover>

        <select
          aria-label="Sort results"
          className={selectClass}
          value={state.sort ?? ''}
          onChange={(e) =>
            onChange(
              withValidSort({
                ...state,
                sort: (e.target.value || undefined) as StockSearchState['sort'],
              }),
            )
          }
        >
          <option value="">Best match</option>
          {Object.entries(SORT_LABELS)
            .filter(([v]) => v !== 'relevance')
            .map(([value, text]) => (
              <option key={value} value={value} disabled={value === 'distance' && !state.postcode}>
                {text}
              </option>
            ))}
        </select>

        <select
          aria-label="Stock source"
          className={selectClass}
          value={source}
          onChange={(e) => onSourceChange(e.target.value)}
        >
          <option value="all">All sources</option>
          <option value="dealerkit">DealerKit</option>
          {connectors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {chips.length > 0 && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible">
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="bg-secondary text-secondary-foreground inline-flex shrink-0 items-center gap-1 rounded-full py-1 pr-1 pl-3 text-xs"
            >
              {chip.label}
              <button
                type="button"
                aria-label={`Remove ${chip.label}`}
                onClick={() => onChange(withValidSort({ ...state, [chip.key]: undefined }))}
                className="hover:bg-background/60 focus-visible:ring-ring cursor-pointer rounded-full p-1 focus-visible:ring-2"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 text-xs"
            onClick={() => onChange(withValidSort({ sort: state.sort }))}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};
